(() => {
  const cart = document.querySelector('.theme-main-cart');
  const error = cart?.querySelector('[data-theme-cart-quantity-error]');
  if (!cart || !error) return;

  const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
  const cartJsonUrl = `${cartUrl}.js`;
  const sectionId = cart.dataset?.themeCartSectionId;
  if (!sectionId) return;
  let renderRevision = 0;
  let pending = false;
  let needsRecovery = false;
  let queued = null;
  let queuedTimer = null;
  let reconcileDrawer = () => {};
  let externalRefreshRequested = false;
  let externalRefreshPending = false;

  const showError = (message) => {
    const status = cart.querySelector('[data-theme-cart-status]');
    if (status) status.textContent = '';
    error.textContent = message;
    error.hidden = false;
  };

  const clearError = () => {
    error.textContent = '';
    error.hidden = true;
  };

  const requireRecovery = (message) => {
    needsRecovery = true;
    showError(`${message} Refresh your cart before continuing.`);
    buttons().forEach((button) => { button.disabled = true; });
    const checkout = cart.querySelector('[name="checkout"]');
    if (checkout) checkout.disabled = true;
    const retry = cart.querySelector('[data-theme-cart-retry]');
    if (retry) retry.hidden = false;
  };

  const matchesRenderedCart = (state) => {
    if (!Array.isArray(state?.items)) return false;
    const rows = [...cart.querySelectorAll('.theme-cart-line-item[data-theme-item-key]')];
    const itemsByKey = new Map(state.items.map((item) => [item.key, item]));
    return Number.isSafeInteger(state.total_price) && Number(cart.dataset.themeCartTotal) === state.total_price &&
      rows.length === state.items.length && rows.every((row) =>
      itemsByKey.get(row.dataset.themeItemKey)?.quantity === Number(row.dataset.themeCurrentQuantity)
    );
  };

  const applySection = (html) => {
    if (typeof html !== 'string') throw new Error('Cart section unavailable');
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.theme-main-cart');
    const count = Number(source?.dataset.themeCartItemCount);
    if (!source || source.dataset.themeCartSectionId !== sectionId ||
        !Number.isSafeInteger(count) || count < 0 || !source.querySelector('[data-theme-cart-quantity-error]')) {
      throw new Error('Cart section invalid');
    }
    ++renderRevision;
    const focus = document.activeElement;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    window.ThemeCartRender.reconcile(cart, source);
    document.querySelectorAll('[data-theme-cart-count]').forEach((node) => { node.textContent = String(count); });
    document.dispatchEvent(new CustomEvent('theme:cart:render', { detail: { cart } }));
    if (focus?.isConnected) focus.focus({ preventScroll: true });
    else if (cart.contains(document.activeElement) || focus?.closest?.('.theme-main-cart')) {
      cart.querySelector('.theme-cart-line-item__qty-btn, .theme-main-cart__empty-actions a')?.focus({ preventScroll: true });
    }
    window.scrollTo(scrollX, scrollY);
    needsRecovery = false;
  };

  const refreshSection = async () => {
    const revision = ++renderRevision;
    const url = new URL(cartUrl, window.location.origin);
    url.searchParams.set('sections', sectionId);
    try {
      // Shopify negotiates /cart as cart JSON when Accept is application/json,
      // even with ?sections=. Let the sections parameter select the response.
      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Cart unavailable');
      const sections = await response.json();
      if (revision !== renderRevision) return false;
      applySection(sections[sectionId]);
      return true;
    } catch (refreshError) {
      if (revision === renderRevision) throw refreshError;
      return false;
    }
  };

  const refreshExternalCart = async () => {
    if (!externalRefreshRequested || externalRefreshPending || pending) return;
    externalRefreshPending = true;
    externalRefreshRequested = false;
    try {
      const applied = await refreshSection();
      if (applied && !pending) finish();
    } catch (_cartError) {
      requireRecovery('We couldn’t refresh your cart.');
    } finally {
      externalRefreshPending = false;
      refreshExternalCart();
    }
  };
  document.addEventListener('theme:cart:refresh', () => {
    externalRefreshRequested = true;
    refreshExternalCart();
  });

  const reconcileError = async (message) => {
    try {
      const response = await fetch(cartJsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      if (!matchesRenderedCart(await response.json())) {
        await refreshSection();
      }
    } catch (_cartError) {
      requireRecovery(message);
      return;
    }
    showError(message);
  };

  // The modal remains its own controller. Reconcile the page after its close.
  const drawer = document.querySelector('cart-drawer');
  if (drawer && typeof MutationObserver !== 'undefined') {
    let drawerChanged = false;
    let drawerChangeRevision = 0;
    let reconcileTimer;
    reconcileDrawer = () => {
      if (!drawerChanged || drawer.classList.contains('active') || pending) return;
      window.clearTimeout(reconcileTimer);
      reconcileTimer = window.setTimeout(async () => {
        if (pending || cart.isConnected === false) return;
        const revision = ++renderRevision;
        const changedRevision = drawerChangeRevision;
        const current = () => revision === renderRevision && changedRevision === drawerChangeRevision &&
          !pending && !drawer.classList.contains('active') && cart.isConnected !== false;
        let state;
        try {
          const response = await fetch(cartJsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Cart unavailable');
          state = await response.json();
        } catch (_cartError) {
          if (current()) requireRecovery('We couldn’t refresh your cart.');
          return;
        }
        if (!current()) return;
        try {
          if (!matchesRenderedCart(state) || Number(cart.dataset.themeCartTotal) !== state.total_price) {
            if (!await refreshSection()) return;
          }
          if (changedRevision === drawerChangeRevision) drawerChanged = false;
        } catch (_cartError) {
          // refreshSection rejects only a failure that still owns its revision.
          if (changedRevision === drawerChangeRevision && !drawer.classList.contains('active') &&
              cart.isConnected !== false) requireRecovery('We couldn’t refresh your cart.');
        }
      }, 140);
    };
    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList' || mutation.type === 'characterData' ||
          mutation.attributeName === 'data-cart-count')) {
        drawerChanged = true;
        ++drawerChangeRevision;
      }
      reconcileDrawer();
    }).observe(drawer, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-cart-count'] });
  }

  const buttons = () => [...cart.querySelectorAll('.theme-cart-line-item__qty-form button[type="submit"]')];
  const finish = () => {
    pending = false;
    cart.removeAttribute('aria-busy');
    buttons().forEach((button) => { button.disabled = needsRecovery; });
    reconcileDrawer();
    refreshExternalCart();
  };

  cart.addEventListener('click', async (event) => {
    const retry = event.target.closest?.('[data-theme-cart-retry]');
    if (!retry || pending) return;
    pending = true;
    retry.disabled = true;
    try {
      await refreshSection();
    } catch (_cartError) {
      requireRecovery('We couldn’t refresh your cart.');
    } finally {
      retry.disabled = false;
      finish();
    }
  });

  const submitQueued = async () => {
    const request = queued;
    queued = null;
    queuedTimer = null;
    if (!request || request.quantity === request.currentQuantity) {
      finish();
      return;
    }
    buttons().forEach((button) => { button.disabled = true; });
    try {
      const response = await fetch(`${request.action}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...(request.key ? { id: request.key } : { line: request.line }), quantity: request.quantity,
          ...(sectionId ? { sections: [sectionId], sections_url: cartUrl } : {}) })
      });
      const result = await response.json().catch(() => null);
      if (response.ok && Array.isArray(result?.items) && !(Number(result?.status) >= 400)) {
        try { applySection(result.sections?.[sectionId]); }
        catch (_sectionError) { await refreshSection(); }
        const confirmed = [...cart.querySelectorAll('.theme-cart-line-item')]
          .find((row) => row.dataset.themeItemKey === request.key);
        const quantity = confirmed ? Number(confirmed.dataset.themeCurrentQuantity) : 0;
        if (quantity !== request.quantity) {
          showError(window.cartStrings?.quantityError?.replace('[quantity]', quantity) ||
            `Only ${quantity} of this item is available.`);
        } else {
          window.ThemeCartRender.announce(cart.querySelector('[data-theme-cart-status]'), { title: request.title, quantity });
        }
        return;
      }
      const message = [result?.description, typeof result?.errors === 'string' ? result.errors : '', result?.message]
        .find((value) => typeof value === 'string' && value.trim()) ||
        `We couldn’t update the quantity (error ${response.status}). Please try again.`;
      await reconcileError(message);
    } catch (_requestError) {
      await reconcileError('We couldn’t update the quantity. Please try again.');
    } finally {
      finish();
      if (request.focus?.isConnected) request.focus.focus({ preventScroll: true });
      else if (request.focus) cart.querySelector('.theme-cart-line-item__qty-btn, .theme-main-cart__empty-actions a')?.focus({ preventScroll: true });
    }
  };

  cart.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches?.('.theme-cart-line-item__qty-form')) return;
    event.preventDefault();
    if (needsRecovery) return;
    if (pending && !queued) return;

    const row = form.closest('.theme-cart-line-item');
    const key = String(form.elements.namedItem('id')?.value || '').trim();
    const line = Number(form.elements.namedItem('line')?.value);
    const currentQuantity = Number(row?.dataset.themeCurrentQuantity);
    const formQuantity = Number(form.elements.namedItem('quantity')?.value);
    const delta = formQuantity - currentQuantity;
    if (!row || (!key && (!Number.isSafeInteger(line) || line < 1)) ||
        !Number.isSafeInteger(currentQuantity) || !Number.isSafeInteger(formQuantity) ||
        (delta !== -1 && delta !== 1)) {
      showError('We couldn’t identify this cart item. Please refresh your cart and try again.');
      return;
    }

    if (queued && queued.row !== row) return;
    if (!queued) {
      ++renderRevision;
      clearError();
      const status = cart.querySelector('[data-theme-cart-status]');
      if (status) status.textContent = '';
      pending = true;
      cart.setAttribute('aria-busy', 'true');
      queued = { row, key, line, title: row.querySelector('.cart-item__name')?.textContent.trim(), action: form.action, quantity: currentQuantity, currentQuantity, focus: event.submitter || document.activeElement };
      buttons().forEach((button) => { if (!row.contains(button)) button.disabled = true; });
    }
    queued.quantity = Math.max(0, queued.quantity + delta);
    window.clearTimeout(queuedTimer);
    queuedTimer = window.setTimeout(submitQueued, 140);
  });
})();
